import {
  UpdateRFSSubmissionPayloadSchema,
  updateRFSSubmissionPayloadSchema,
  useUpdateRFSSubmission,
} from "@/api/generate-hpdb";
import { useStatusIntegrationXLs } from "@/api/master-param";
import { useRFS } from "@/api/rfs";
import { FieldCombobox } from "@/components/form/field-combobox";
import { FieldInput } from "@/components/form/field-input";
import { FieldInputDate } from "@/components/form/field-input-date";
import { FieldTextarea } from "@/components/form/field-textarea";
import { FormContainer } from "@/components/form/form-container";
import { SubmitButton } from "@/components/ui/button";
import { InputWithLabel } from "@/components/ui/input-with-label";
import { useCurrentUser } from "@/features/auth/useCurrentUser";
import { MISSING_ID_IN_ROUTE, ROLE, STATUS } from "@/utils/constant";
import { swalError, swalSuccess } from "@/utils/lib/swal";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

interface UpdateRFSSubmissionFormProps {
  isReadOnly?: boolean;
}

export function UpdateRFSSubmissionForm({
  isReadOnly,
}: UpdateRFSSubmissionFormProps) {
  const [statusSearch, setStatusSearch] = useState("");

  const { id } = useParams();
  if (!id) {
    throw new Error(MISSING_ID_IN_ROUTE);
  }

  const { data: rfs } = useRFS({ id });
  const { data: statusIntegrations } = useStatusIntegrationXLs({
    search: statusSearch,
  });
  const { roles } = useCurrentUser();

  const form = useForm<UpdateRFSSubmissionPayloadSchema>({
    resolver: zodResolver(updateRFSSubmissionPayloadSchema),
    values: {
      statusRfsSubmissionLms: {
        id: rfs?.statusRfsSubmissionLms ? rfs?.statusRfsSubmissionLms.id : "",
      },
      resultRemarks: rfs?.resultRemarks ?? "",
      resultTotalResidenceHp: rfs?.resultTotalResidenceHp ?? 0,
      resultTotalBizzHp: rfs?.resultTotalBizzHp ?? 0,
      resultTotalHpRfs: rfs?.resultTotalHpRfs ?? 0,
      resultDate: rfs?.resultDate ?? "",
    },
  });

  const { mutate, isPending } = useUpdateRFSSubmission();

  const isAdministrator = roles?.includes(ROLE.ADMINISTRATOR);
  const isReadOnlyStatus =
    (rfs?.statusRfsSubmissionLms?.value ===
      STATUS.LMS_SUBMISSION.VALUE.REJECTED_BY_TENANT ||
      rfs?.statusRfsSubmissionLms?.value ===
        STATUS.LMS_SUBMISSION.VALUE.APPROVED_BY_TENANT) &&
    !isAdministrator;

  const totalResidenceHomepass = parseInt(
    (form.watch("resultTotalResidenceHp") as unknown as string) || "0",
    10,
  );
  const totalBizzHomepass = parseInt(
    (form.watch("resultTotalBizzHp") as unknown as string) || "0",
    10,
  );
  const totalHpRfs = totalResidenceHomepass + totalBizzHomepass;
  form.setValue("resultTotalHpRfs", totalHpRfs);

  const handleSubmit = (values: UpdateRFSSubmissionPayloadSchema) => {
    mutate(
      {
        id: id,
        data: {
          statusRfsSubmissionLms: {
            id: values.statusRfsSubmissionLms.id,
          },
          resultRemarks: values.resultRemarks,
          resultTotalResidenceHp: values.resultTotalResidenceHp,
          resultTotalBizzHp: values.resultTotalBizzHp,
          resultTotalHpRfs: values.resultTotalHpRfs,
          resultDate: values.resultDate,
        },
      },
      {
        onSuccess: () => {
          swalSuccess.fire({
            text: "Updated RFS Submission successfully",
          });
        },
        onError: (error) => {
          swalError.fire({
            text: error.message,
          });
        },
      },
    );
  };

  return (
    <FormContainer form={form} onSubmit={handleSubmit}>
      <FieldCombobox
        name="statusRfsSubmissionLms.id"
        label="Updated RFS Submission"
        search={statusSearch}
        setSearch={setStatusSearch}
        options={
          statusIntegrations?.content?.map((statusIntegration) => ({
            label: statusIntegration.value,
            value: statusIntegration.id,
          })) ?? []
        }
        hideLabel
        isDisabled={isReadOnlyStatus}
      />
      <div className="grid grid-cols-3 gap-x-4">
        <FieldInput
          name="resultTotalResidenceHp"
          label="Total Residence Homepass"
          type="number"
          hasRequiredAsterisk
          isDisabled={!isAdministrator}
        />
        <FieldInput
          name="resultTotalBizzHp"
          label="Total Bizz Homepass"
          type="number"
          hasRequiredAsterisk
          isDisabled={!isAdministrator}
        />
        <InputWithLabel
          name="resultTotalHpRfs"
          value={totalHpRfs}
          label="Total HP RFS (ABD)"
          isDisabled
          hasRequiredAsterisk
        />
      </div>
      <FieldInputDate
        label="Date"
        name="resultDate"
        onChange={(date) => {
          if (date === undefined) {
            // @ts-expect-error null
            form.setValue("resultDate", null);
          }
        }}
        hasRequiredAsterisk
        minDate={new Date()}
      />
      <FieldTextarea
        name="resultRemarks"
        label="Remarks"
        hasRequiredAsterisk
        isDisabled={!isAdministrator}
      />
      <SubmitButton
        loading={isPending}
        disabled={isReadOnly}
        className="ml-auto"
      />
    </FormContainer>
  );
}
